import axiosNest from '../api/axiosNest';
import { toErrorMessage } from '../admin/userOffers/orderActionsApi';

/**
 * Kontrahent z kartoteki na prezleceniu — klient API (makieta back office
 * v2, fala B, 21.10.2026; przełącznik `preOrderContractor`, S-21.10-10;
 * BR-ADM-11, BR-ADM-13). Reguły i zdania są w API
 * (`pre-order/pre-order-contractor.ts`) — ekran je pokazuje.
 */

/** Klucz przełącznika (rejestr `feature-flags.ts` w API). */
export const FEATURE_PRE_ORDER_CONTRACTOR = 'preOrderContractor';

/** Pole historii prezlecenia i dziennika zlecenia — dane z API. */
export const FIELD_CONTRACTOR = 'contractorId';

export interface ContractorRef {
  id: number;
  legalName: string;
  taxId: string | null;
  tmsCustomerId: number | null;
  city: string | null;
  country: string | null;
}

export interface VatSuggestionView {
  rate: string | null;
  reason: string;
  certain: boolean;
  missing: string[];
}

/** `GET /pre-orders/:id/contractor` (i `contractor` w oknie kontroli). */
export interface PreOrderContractorView {
  chosen: ContractorRef | null;
  chosenLabel: string | null;
  effective: ContractorRef | null;
  effectiveLabel: string | null;
  source: 'preOrder' | 'tms' | 'bankRow' | null;
  sourceLabel: string;
  mismatch: string | null;
  branchNote: string | null;
  vat: VatSuggestionView;
  changeRefusal: string | null;
}

/** Kontrahent słowem — ten sam zapis co w API (`contractorLabel`). */
export function contractorLabel(c: ContractorRef): string {
  const place = [c.city, c.country ? `(${c.country})` : null].filter(Boolean).join(' ');
  return [
    c.legalName,
    c.taxId ? `NIP ${c.taxId}` : null,
    c.tmsCustomerId !== null && c.tmsCustomerId !== undefined ? `nr klienta TMS ${c.tmsCustomerId}` : null,
    place || null,
  ]
    .filter(Boolean)
    .join(' · ');
}

/** Ile podpowiedzi pokazuje wyszukiwarka. */
export const SEARCH_LIMIT = 10;

/**
 * Wyszukiwarka kartoteki — po nazwie, NIP, mieście, a sama liczba także po
 * numerze klienta w TMS (`contractors/filters.ts`). Pusty tekst — nic.
 */
export async function searchContractors(q: string): Promise<ContractorRef[]> {
  const query = q.trim();
  if (query === '') return [];
  const { data } = await axiosNest.get<ContractorRef[]>('/contractors', {
    params: {
      filter: JSON.stringify({ q: query }),
      range: JSON.stringify([0, SEARCH_LIMIT - 1]),
      sort: JSON.stringify(['legalName', 'ASC']),
    },
  });
  return Array.isArray(data) ? data : [];
}

export async function fetchPreOrderContractor(
  preOrderId: number | string,
): Promise<PreOrderContractorView> {
  const { data } = await axiosNest.get<PreOrderContractorView>(`/pre-orders/${preOrderId}/contractor`);
  return data;
}

/** Wybór (albo `null` — sama nazwa klienta) z opcjonalnym powodem. */
export async function setPreOrderContractor(
  preOrderId: number | string,
  contractorId: number | null,
  reason?: string,
): Promise<PreOrderContractorView> {
  const { data } = await axiosNest.put<PreOrderContractorView>(`/pre-orders/${preOrderId}/contractor`, {
    contractorId,
    ...(reason && reason.trim() !== '' ? { reason: reason.trim() } : {}),
  });
  return data;
}

export { toErrorMessage };
